/*
 * Every colour, space, radius, font and shadow here is a design token.
 * The only literals are structural hairlines.
 */

.list,
.list * {
  box-sizing: border-box;
}

.list {
  /* The UA sheet centres a popover with inset:0 + margin:auto. Both are cleared so the
     measured top/left land where they're put. */
  inset: auto;
  margin: 0;
  padding: var(--space-1);

  min-width: 180px;
  max-width: 320px;
  border: 1px solid var(--border-default);
  border-radius: var(--radius-md);
  background: var(--bg-elevated);
  box-shadow: var(--shadow-lg);

  font-family: var(--font-sans);

  /* Not on :popover-open, even though only an open popover is ever displayed.
     allow-discrete holds `display: flex` through the exit transition, but neither of
     these is transitioned — on :popover-open they'd snap back the instant the attribute
     goes, and the items would lay out in a row while the menu faded. Same trap as
     Modal's flex-direction; anything state-dependent about layout belongs here. */
  flex-direction: column;
  gap: 1px;

  opacity: 0;
  translate: 0 calc(var(--space-1) * -1);
  transition:
    opacity var(--duration-fast) var(--ease-default),
    translate var(--duration-fast) var(--ease-default),
    overlay var(--duration-fast) allow-discrete,
    display var(--duration-fast) allow-discrete;
}

/* Only the discrete flip and the animated end state — see above. */
.list:popover-open {
  display: flex;
  opacity: 1;
  translate: 0 0;
}

/* The "before" frame — a freshly shown element has no previous style to animate from. */
/*noinspection CssUnknownProperty*/
@starting-style {
  .list:popover-open {
    opacity: 0;
    translate: 0 calc(var(--space-1) * -1);
  }
}

/* ── Items ── */

.item {
  display: flex;
  align-items: center;
  gap: var(--space-2);
  width: 100%;

  padding: var(--space-2) var(--space-3);
  border: 0;
  border-radius: var(--radius-sm);
  background: none;
  cursor: pointer;

  font-family: inherit;
  font-size: var(--text-sm);
  font-weight: var(--weight-semi);
  line-height: var(--leading-normal);
  text-align: left;
  white-space: nowrap;
  color: var(--text-secondary);

  transition:
    background-color var(--duration-fast) var(--ease-default),
    color var(--duration-fast) var(--ease-default);
}

/* Sized in em against the label, as in Badge and Tabs — no slot prop, no pixel size at
   every call site. An unsized SVG is 300x150. */
.item > svg {
  width: 1.15em;
  height: 1.15em;
  flex-shrink: 0;
}

.item:hover:not(:disabled) {
  background-color: var(--bg-hover);
  color: var(--text-primary);
}

.item:focus-visible {
  outline: var(--focus-ring);
  /* Inset: the list's own padding is only --space-1, so an outward ring would be
     clipped by the rounded border. */
  outline-offset: -2px;
}

.item:disabled {
  color: var(--text-disabled);
  cursor: not-allowed;
}

.danger {
  color: var(--color-danger);
}

.danger:hover:not(:disabled) {
  background-color: var(--color-danger-muted);
  color: var(--color-danger);
}

@media (prefers-reduced-motion: reduce) {
  .list,
  .item {
    transition: none;
  }
}
